import type { BillingCurrency } from '@contracts/payment/Currency';
import { roundToTwoDecimalsString } from '@contracts/utils/RoundingAmount';

/**
 * @deprecated
 * You may try to flatten the fees inside Billing, and use BillingRow instead of BillingColumn and BillingTable
 */
export interface BillingTable {
  CAD: BillingColumn;
  USD: BillingColumn;
}

export interface BillingRow {
  CAD: number;
  USD: number;
}

export const BILLING_DISPLAYED_DECIMALS = 2;

/**
 * @deprecated
 * You may try to flatten the fees inside Billing, and use BillingRow instead of BillingColumn and BillingTable
 */
export interface BillingColumn {
  otherFee: number;
  totalCollected?: number;
}

export interface BillingTableWithBalance {
  CAD: BillingColumnWithBalance;
  USD: BillingColumnWithBalance;
}

interface BillingColumnWithBalance {
  otherFee: number;
  totalCollected: number;
}

export class Billing {
  public readonly billingTable: BillingTable;
  public readonly stateFees: BillingRow;
  public readonly serviceFees: BillingRow;
  public readonly adminFees: BillingRow;
  public readonly taxes: BillingRow;
  public readonly totalFees: BillingRow;
  public readonly totalRemaining?: BillingRow;

  constructor({
    billingTable,
    adminFees,
    taxes,
    totalFees,
    stateFees,
    serviceFees,
    totalRemaining,
  }: {
    billingTable: BillingTable;
    adminFees: BillingRow;
    taxes: BillingRow;
    totalFees: BillingRow;
    stateFees: BillingRow;
    serviceFees: BillingRow;
    totalRemaining?: BillingRow;
  }) {
    this.billingTable = billingTable;
    this.adminFees = adminFees;
    this.taxes = taxes;
    this.totalFees = totalFees;
    this.totalRemaining = totalRemaining;
    this.stateFees = stateFees;
    this.serviceFees = serviceFees;
  }

  getTotalFees(): BillingRow {
    return this.totalFees;
  }

  getTotalRemaining(): BillingRow | undefined {
    return this.totalRemaining;
  }

  getRoundedTotalRemaining(): BillingRow | undefined {
    return {
      CAD: roundToTwoDecimals(this.getTotalRemaining()!.CAD),
      USD: roundToTwoDecimals(this.getTotalRemaining()!.USD),
    };
  }

  hasSurcharge() {
    return (
      this.shouldDisplayTheBalance(this.billingTable) &&
      (this.getRoundedTotalRemaining()!.CAD < 0 ||
        this.getRoundedTotalRemaining()!.USD < 0)
    );
  }

  hasDuePayment = () => {
    return (
      this.shouldDisplayTheBalance(this.billingTable) &&
      (this.getRoundedTotalRemaining()!.CAD > 0 ||
        this.getRoundedTotalRemaining()!.USD > 0)
    );
  };

  calculateMaxBillable = ({
    currency,
  }: {
    currency?: BillingCurrency;
  } = {}): number => {
    const usdRemaining = this.getTotalRemaining()?.USD;
    const cadRemaining = this.getTotalRemaining()?.CAD;
    if (usdRemaining !== undefined && cadRemaining !== undefined) {
      return +(currency === 'USD'
        ? roundToTwoDecimalsString(usdRemaining)
        : roundToTwoDecimalsString(cadRemaining));
    }
    return 0;
  };

  shouldDisplayTheBalance(
    billingTable: BillingTable,
  ): billingTable is BillingTableWithBalance {
    return (
      this.getTotalRemaining()?.CAD !== undefined &&
      this.getTotalRemaining()?.USD !== undefined &&
      billingTable.CAD.totalCollected !== undefined &&
      billingTable.USD.totalCollected !== undefined
    );
  }
}

export const isCAD = (currency: BillingCurrency) =>
  currency === 'CAD' || currency === 'ALL';
export const isUSD = (currency: BillingCurrency) =>
  currency === 'USD' || currency === 'ALL';

const roundToTwoDecimals = (amount: number) => {
  return parseFloat(amount.toFixed(BILLING_DISPLAYED_DECIMALS));
};
